import Lightbox from 'bs5-lightbox'

function triggerSrc(el) {
  return el.dataset.src || el.dataset.remote || el.getAttribute('href')
}

function srcPathname(value) {
  if (!value) return null
  try {
    return new URL(value, window.location.href).pathname
  } catch {
    return null
  }
}

// The trigger elements carry the attachment's pixel dimensions (see
// ImageHelper#lightbox_dimensions). Collect them keyed by the path of the image
// they point at, so each carousel slide can be matched back to its trigger.
function triggerDimensionsByPath(trigger) {
  const gallery = trigger.dataset.gallery
  const triggers = gallery
    ? document.querySelectorAll(`[data-gallery="${gallery}"]`)
    : [trigger]
  const dimensions = new Map()

  triggers.forEach(el => {
    const width = parseInt(el.dataset.imageWidth, 10)
    const height = parseInt(el.dataset.imageHeight, 10)
    if (!width || !height) return

    const src = triggerSrc(el)
    const pathname = srcPathname(src)
    if (!pathname) return

    dimensions.set(pathname, { width, height })
  })

  return dimensions
}

function sizeOfImage(img, dimensions) {
  if (!img) return null
  const pathname = srcPathname(img.getAttribute('src') || img.src)
  if (pathname && dimensions.has(pathname)) return dimensions.get(pathname)
  if (img.naturalWidth > 0 && img.naturalHeight > 0) {
    return { width: img.naturalWidth, height: img.naturalHeight }
  }
  return null
}

function remPx(n) {
  return n * (parseFloat(getComputedStyle(document.documentElement).fontSize) || 16)
}

// Viewport box the image may occupy: dialog margins plus the one-line header.
function availableLightboxBox(modal) {
  const dialog = modal.querySelector('.modal-dialog')
  const header = modal.querySelector('.lightbox-header')
  const fallbackHeader = remPx(3.5) + 1
  let marginX
  let marginY
  let headerH

  if (dialog && dialog.isConnected) {
    const cs = getComputedStyle(dialog)
    marginX = parseFloat(cs.marginLeft) + parseFloat(cs.marginRight)
    marginY = parseFloat(cs.marginTop) + parseFloat(cs.marginBottom)
    headerH = header ? header.getBoundingClientRect().height : 0
    if (!headerH) headerH = fallbackHeader
  } else {
    const margin = remPx(window.innerWidth < 576 ? 0.5 : 1.75)
    marginX = margin * 2
    marginY = margin * 2
    headerH = fallbackHeader
  }

  return {
    width: Math.max(1, window.innerWidth - marginX),
    height: Math.max(1, window.innerHeight - marginY - headerH)
  }
}

function displaySize(modal, size) {
  if (!size || !size.width || !size.height) return null
  const box = availableLightboxBox(modal)
  const scale = Math.min(1, box.width / size.width, box.height / size.height)
  return {
    width: Math.max(1, Math.round(size.width * scale)),
    height: Math.max(1, Math.round(size.height * scale))
  }
}

// Size the dialog and the carousel frame from the current image. The <img>
// uses object-fit: contain inside that frame, so a portrait does not stretch
// when the frame grows for a landscape neighbour.
function sizeDialogToImage(modal, size) {
  const dialog = modal.querySelector('.modal-dialog')
  const inner = modal.querySelector('.carousel-inner')
  if (!dialog) return

  const frame = displaySize(modal, size)
  if (!frame) {
    dialog.style.width = ''
    if (inner) inner.style.height = ''
    return
  }

  dialog.style.width = `${frame.width}px`
  if (inner) inner.style.height = `${frame.height}px`
}

function decorateImage(img, size) {
  // bs5-lightbox pins the size with !important utilities, which an inline
  // style cannot override. The stylesheet restates them.
  img.classList.remove('mw-100', 'mh-100', 'w-auto', 'h-auto', 'img-fluid')
  img.classList.add('lightbox-image-sized')
  img.loading = 'eager'
  img.width = size.width
  img.height = size.height
}

function preloadCarouselImages(modal) {
  modal.querySelectorAll('.lightbox-carousel img').forEach(img => {
    img.loading = 'eager'
    if (!img.src) return
    const preloader = new Image()
    preloader.src = img.src
  })
}

// bs5-lightbox renders its <img> without width or height, so until the image
// arrives it contributes no size. Size the dialog and the carousel frame from
// the trigger's pixel dimensions so the modal opens at the size it will keep.
function applyKnownDimensions(modal, trigger) {
  const dimensions = triggerDimensionsByPath(trigger)
  const sizesInOrder = [...dimensions.values()]
  const imgs = [...modal.querySelectorAll('.lightbox-carousel img')]
  if (imgs.length === 0) return false

  imgs.forEach((img, index) => {
    const size = sizeOfImage(img, dimensions) || sizesInOrder[index]
    if (size) decorateImage(img, size)
  })

  const activeImg = modal.querySelector('.carousel-item.active img') || imgs[0]
  const activeIndex = Math.max(0, imgs.indexOf(activeImg))
  const activeSize = sizeOfImage(activeImg, dimensions) ||
    (activeImg && activeImg.width && activeImg.height
      ? { width: activeImg.width, height: activeImg.height }
      : null) ||
    sizesInOrder[activeIndex]
  if (activeSize) {
    sizeDialogToImage(modal, activeSize)
    return true
  }

  return false
}

// bs5-lightbox always marks the first slide active and calls carousel.to()
// while the modal is still detached, which Bootstrap ignores — so a click on
// the second thumbnail opened the first image. Set the matching slide active
// from the trigger href before show().
function activateClickedSlide(modal, trigger) {
  const targetPath = srcPathname(triggerSrc(trigger))
  const items = [...modal.querySelectorAll('.carousel-item')]
  if (items.length === 0) return

  let index = 0
  if (targetPath) {
    const found = items.findIndex(item => {
      const img = item.querySelector('img')
      return img && srcPathname(img.getAttribute('src') || img.src) === targetPath
    })
    if (found >= 0) index = found
  }

  items.forEach((item, i) => {
    item.classList.toggle('active', i === index)
  })
}

function captionText(item) {
  if (!item) return ''
  const caption = item.querySelector('.lightbox-caption')
  return caption ? caption.textContent.trim() : ''
}

// Keep the filename in a header that is not a carousel slide. Putting it on
// each item meant ArrowRight translated the bar off-screen when the slide
// animation ran.
function installLightboxHeader(modal) {
  const content = modal.querySelector('.modal-content')
  if (!content || content.querySelector('.lightbox-header')) return

  const header = document.createElement('div')
  header.className = 'lightbox-header'
  const title = document.createElement('div')
  title.className = 'lightbox-header-title'
  header.appendChild(title)
  const closeBtn = modal.querySelector('.btn-close')
  if (closeBtn) header.appendChild(closeBtn)
  content.prepend(header)

  const sync = item => {
    title.textContent = captionText(item || modal.querySelector('.carousel-item.active'))
  }
  sync()

  const carousel = modal.querySelector('.lightbox-carousel')
  if (!carousel) return

  carousel.addEventListener('slide.bs.carousel', event => {
    sync(event.relatedTarget)
  })
}

function bindSlideSizing(modal, trigger) {
  const carousel = modal.querySelector('.lightbox-carousel')
  if (!carousel) return

  const dimensions = triggerDimensionsByPath(trigger)
  carousel.addEventListener('slide.bs.carousel', event => {
    const img = event.relatedTarget && event.relatedTarget.querySelector('img')
    if (!img) return

    const apply = () => {
      const items = [...modal.querySelectorAll('.carousel-item')]
      const index = items.indexOf(event.relatedTarget)
      const size = sizeOfImage(img, dimensions) ||
        (img.width && img.height ? { width: img.width, height: img.height } : null) ||
        [...dimensions.values()][index]
      if (size) {
        decorateImage(img, size)
        sizeDialogToImage(modal, size)
      }
    }

    apply()
    if (img.naturalWidth === 0) {
      img.addEventListener('load', () => {
        // Flipping on before the image arrived leaves another slide showing,
        // whose size the frame keeps.
        if (event.relatedTarget.classList.contains('active')) apply()
      }, { once: true })
    }
  })
}

// Bootstrap ignores prev and next while a slide is animating, so every press
// during the 0.6s fade was lost and flipping through a gallery meant waiting
// out each fade. Without .slide it swaps the slides at once and counts every
// press. The fade is ours instead, a CSS animation that holds nothing up
// (.lightbox-entering in components.sass): a press mid-fade starts the next
// one straight away. Marked on slide, not slid: slid also fires, as the modal
// opens, for the carousel.to() bs5-lightbox calls while building it, and would
// fade the clicked image in over the first one.
function bindSlideFade(modal) {
  const carousel = modal.querySelector('.lightbox-carousel')
  if (!carousel) return

  carousel.classList.remove('slide')
  carousel.addEventListener('slide.bs.carousel', event => {
    const items = [...carousel.querySelectorAll('.carousel-item')]
    items.forEach(item => item.classList.remove('lightbox-entering', 'lightbox-leaving'))
    items[event.from].classList.add('lightbox-leaving')
    event.relatedTarget.classList.add('lightbox-entering')
  })
}

// bs5-lightbox wraps every image in Bootstrap's 16:9 .ratio box. Leaving that
// wrapper in the tree makes height:100% on the <img> resolve against an
// auto-height parent, so a portrait still grows with the dialog width. Hoist
// the image (and any other children) out and drop the box.
function unwrapRatioBoxes(modal) {
  modal.querySelectorAll('.carousel-item .ratio').forEach(el => {
    const parent = el.parentElement
    if (!parent) return
    while (el.firstChild) parent.insertBefore(el.firstChild, el)
    el.remove()
  })
}

// bs5-lightbox puts a Bootstrap spinner in each slide, centered with
// top-50/start-50/translate-middle. Our leftover-wrapper rule stretches
// those wraps to 100%, so the spinner sits at the wrap's top-left — the
// inner top-left of the photo. Drop them and overlay one spinner on the
// sized .carousel-inner instead.
function installLightboxSpinner(modal) {
  modal.querySelectorAll('.lightbox-carousel .spinner-border').forEach(spinner => {
    const wrap = spinner.closest('.position-absolute')
    if (wrap) wrap.remove()
    else spinner.remove()
  })

  const inner = modal.querySelector('.carousel-inner')
  if (!inner) return

  let overlay = inner.querySelector('.lightbox-frame-spinner')
  if (!overlay) {
    overlay = document.createElement('div')
    overlay.className = 'lightbox-frame-spinner'
    overlay.setAttribute('aria-hidden', 'true')
    overlay.innerHTML = '<div class="spinner-border" role="status"></div>'
    inner.appendChild(overlay)
  }

  const imageInSlide = slide => slide && slide.querySelector('img')
  // Failed loads never get naturalWidth > 0. Remember them so a background
  // slide that errors before it is shown does not leave the spinner up forever.
  const finishedImages = new WeakSet()
  const imageIsReady = img => !img || img.naturalWidth > 0 || finishedImages.has(img)

  const sync = incomingSlide => {
    const img = imageInSlide(incomingSlide) ||
      modal.querySelector('.carousel-item.active img') ||
      modal.querySelector('.lightbox-carousel img')
    overlay.classList.toggle('is-visible', !imageIsReady(img))
  }

  modal.querySelectorAll('.lightbox-carousel img').forEach(img => {
    const finish = () => {
      finishedImages.add(img)
      sync()
    }
    img.addEventListener('load', finish, { once: true })
    img.addEventListener('error', finish, { once: true })
  })

  const carousel = modal.querySelector('.lightbox-carousel')
  if (carousel) {
    carousel.addEventListener('slide.bs.carousel', event => {
      sync(event.relatedTarget)
    })
  }

  modal.addEventListener('show.bs.modal', () => sync())
  modal.addEventListener('shown.bs.modal', () => sync())

  sync()
}

// Keep the dialog hidden behind a backdrop-level spinner until the image has
// loaded, so a collapsed dialog is never on screen. Only needed when the
// dimensions are unknown, since otherwise the dialog is already the right size.
function revealWhenLoaded(modal) {
  const dialog = modal.querySelector('.modal-dialog')
  dialog.style.visibility = 'hidden'

  const spinner = document.createElement('div')
  spinner.className = 'lightbox-loading-spinner'
  spinner.innerHTML = '<div class="spinner-border" role="status"></div>'
  modal.appendChild(spinner)

  const img = modal.querySelector('.carousel-item.active img') ||
    modal.querySelector('.lightbox-carousel img')
  const reveal = () => {
    if (img && img.naturalWidth > 0) {
      sizeDialogToImage(modal, { width: img.naturalWidth, height: img.naturalHeight })
    }
    dialog.style.visibility = ''
    spinner.remove()
  }

  // The modal is still detached here, so the image has not started loading and
  // img.complete reports true even though nothing has been decoded yet.
  if (!img || img.naturalWidth > 0) {
    reveal()
    return
  }

  img.addEventListener('load', reveal, { once: true })
  img.addEventListener('error', reveal, { once: true })
}

// Remove bs5-lightbox's auto-bound click handlers (added at import time on
// line 254 of its source) to prevent duplicate modals. We clone each element
// to strip all listeners, then attach only our own handler.
function bindLightboxElements() {
  document.querySelectorAll('[data-bs-toggle="lightbox"]').forEach(el => {
    const clone = el.cloneNode(true)
    el.replaceWith(clone)
    clone.addEventListener('click', function(e) {
      e.preventDefault()
      const lightbox = new Lightbox(this)
      const modal = lightbox.modalElement

      bindSlideFade(modal)
      unwrapRatioBoxes(modal)
      activateClickedSlide(modal, this)
      installLightboxSpinner(modal)
      installLightboxHeader(modal)
      bindSlideSizing(modal, this)
      preloadCarouselImages(modal)
      if (!applyKnownDimensions(modal, this)) revealWhenLoaded(modal)

      lightbox.show()
    })
  })
}

// Post-process lightbox modal DOM to fix bs5-lightbox quirks
function patchLightboxModal(modal) {
  // Replace custom SVG close button with standard BS5 btn-close
  const closeBtn = modal.querySelector('.btn-close')
  if (closeBtn) {
    closeBtn.innerHTML = ''
    closeBtn.removeAttribute('style')
  }

  // Unwrap <em> tags from caption text
  modal.querySelectorAll('.lightbox-caption em').forEach(em => {
    em.replaceWith(...em.childNodes)
  })

  const headerTitle = modal.querySelector('.lightbox-header-title')
  const activeCaption = modal.querySelector('.carousel-item.active .lightbox-caption')
  if (headerTitle && activeCaption && !headerTitle.textContent) {
    headerTitle.textContent = activeCaption.textContent.trim()
  }

  const img = modal.querySelector('.carousel-item.active img')
  if (img && img.naturalWidth > 0) {
    sizeDialogToImage(modal, { width: img.naturalWidth, height: img.naturalHeight })
  }

  // Enable frame transitions only after the first layout, so opening does not
  // animate from an empty box up to the image size.
  modal.classList.add('lightbox-ready')
}

// Re-initialize lightbox after Turbo page loads
document.addEventListener('turbo:load', bindLightboxElements)

// Patch lightbox modal once it's fully shown
document.addEventListener('shown.bs.modal', (e) => {
  if (!e.target.classList.contains('lightbox')) return
  patchLightboxModal(e.target)
})
